import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { BreadcrumbNameContext, type Names, type Store } from './breadcrumbNames';

/**
 * Holds what each record in the trail is CALLED.
 *
 * Its own file, exporting only this component, because a module exporting both
 * a component and hooks defeats fast refresh — the rule
 * `react-refresh/only-export-components` states. The context and the two hooks
 * live in `breadcrumbNames.ts`, which exports no component at all.
 */
export function BreadcrumbNameProvider({ children }: { children: ReactNode }): JSX.Element {
  const [names, setNames] = useState<Names>({});

  const value = useMemo<Store>(
    () => ({
      names,
      publish: (id, name) =>
        setNames((prev) => (prev[id] === name ? prev : { ...prev, [id]: name })),
      withdraw: (id) =>
        setNames((prev) => {
          if (!(id in prev)) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        }),
    }),
    [names],
  );

  return (
    <BreadcrumbNameContext.Provider value={value}>{children}</BreadcrumbNameContext.Provider>
  );
}
